import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Card,
  Timeline,
  Checkbox,
  Modal,
  Input,
  InputNumber,
  message,
  Spin,
  Row,
  Col,
  Divider,
  Tooltip,
  Popconfirm,
  Alert,
  Select,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  ToolOutlined,
  RollbackOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getMR, submitMR, recallMR, approveLines, amendMRLine, setLineCostCenter, costCenterLabel, requestMRLineCancel, decideMRLineCancel } from '../../api';
import RefineLineModal from '../../components/RefineLineModal';
import { buildMrPrintHtml } from '../../utils/mrPrint';
import { useAuth } from '../../context/AuthContext';
import PartNumberCombobox from '../../components/PartNumberCombobox';
import CostCenterSelect from '../../components/CostCenterSelect';
import EntityHistory from '../../components/EntityHistory';
import {
  ItemCell,
  MoneyCell,
  VendorCell,
  MissingValue,
  StatusPill,
  DensityToggle,
  useDensity,
} from '../../components/table-cells';
import { useItemPictures } from '../../hooks/useItemPictures';
import { PrefillInfo, onPrefill, prefillLabelParts, historyLabel, newerPurchase, fmtPrice } from '../../utils/priceSource';

const { Title } = Typography;
const { TextArea } = Input;

const STATUS_TAG_COLORS: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  'Partially Approved': 'orange',
  Approved: 'green',
  Rejected: 'red',
  Ordered: 'purple',
  'Partially Ordered': 'geekblue',
  Received: 'cyan',
  Closed: 'default',
};

const URGENCY_TAG_COLORS: Record<string, string> = {
  Low: 'default',
  Normal: 'blue',
  High: 'orange',
  Critical: 'red',
};

/** A line with no catalog code: a stated gap where the code chip would sit. */
const freeTextMark = (
  <span
    style={{
      display: 'inline-block',
      padding: '0 6px',
      fontSize: 11,
      lineHeight: '18px',
      color: '#8a94a6',
      border: '1px dashed #c9cede',
      borderRadius: 4,
      whiteSpace: 'nowrap',
    }}
  >
    free text
  </span>
);

interface MRLine extends PrefillInfo {
  id: number;
  lineNo: number;
  partNumber?: string;
  itemDescription: string;
  vendor: { id: number; name: string } | null;
  category: { id: number; name: string } | null;
  unit: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  lineApprovalStatus: string;
  lineStatus?: string;
  lineNotes?: string;
  rejectReason?: string;
  costCenterId?: number | null;
  costCenter?: any;
  poLines?: Array<{
    actualUnitPrice: number | null;
    actualLineTotal: number | null;
    priceVariance: number | null;
    totalReceived: number | null;
    balanceQty: number | null;
    poHeader?: { id: number; poId: string; status: string } | null;
  }>;
  // Refinement fields
  origPartNumber?: string | null;
  origItemDescription?: string | null;
  origQty?: number | null;
  origUnitPrice?: number | null;
  origUnitId?: number | null;
  origCurrencyId?: number | null;
  origVendorId?: number | null;
  origCategoryId?: number | null;
  refinedBy?: { id: number; displayName: string } | null;
  refinedAt?: string | null;
  refinementNotes?: string | null;
  catalogItemId?: number | null;
  catalogItem?: { id: number; partNumber: string; description: string; descriptionAr?: string | null; unit?: { id: number; name: string } | null } | null;
  // MR line cancellation (2026-09-15)
  cancelStatus?: 'Pending' | 'Approved' | 'Rejected' | null;
  cancelReason?: string | null;
  cancelRequestedBy?: { id: number; displayName: string } | null;
  cancelRequestedAt?: string | null;
  cancelDecidedBy?: { id: number; displayName: string } | null;
  cancelDecidedAt?: string | null;
  reorderQty?: number | string | null;
  // backwards compat aliases
  quantity?: number;
  total?: number;
  status?: string;
  notes?: string;
}

interface MRDetail {
  id: number;
  mrId: string;
  department: { id: number; code: string; name: string } | null;
  requestedBy: { id: number; displayName: string } | null;
  approvedBy: { id: number; displayName: string } | null;
  createdAt: string;
  neededBy: string;
  urgency: string;
  status: string;
  notes?: string;
  total: number;
  mrTotal?: number;
  actualTotal?: number;
  lines: MRLine[];
  cancelledTotal?: number;
  netTotal?: number;
  pendingLineCancellations?: number;
  timeline?: Array<{
    action: string;
    userName: string;
    createdAt: string;
    details?: string;
  }>;
}

const MRView: React.FC = () => {
  const { mrId } = useParams<{ mrId: string }>();
  const navigate = useNavigate();
  const { user, isApprover, isAdmin, isProcurement } = useAuth();
  // 2026-09-06 redesign: requesters see code-first lines without vendor / category / actual cost columns.
  const isRequesterView = !(isApprover || isAdmin || isProcurement);
  const [loading, setLoading] = useState(true);
  const [mr, setMr] = useState<MRDetail | null>(null);
  const [selectedLines, setSelectedLines] = useState<Set<number>>(new Set());
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<'selected' | 'single' | null>(null);
  const [amendQtys, setAmendQtys] = useState<Record<number, number>>({});
  const [amendingLineId, setAmendingLineId] = useState<number | null>(null);
  const [singleRejectLineId, setSingleRejectLineId] = useState<number | null>(null);
  const [density, setDensity] = useDensity('mr-view-lines');
  const compact = density === 'compact';

  // MR line cancellation (2026-09-15): direct for the CEO/admin, a request for procurement and the requester.
  const [lineCancel, setLineCancel] = useState<{ line: MRLine; reason: string; direct: boolean } | null>(null);
  const [lineCancelBusy, setLineCancelBusy] = useState(false);
  const submitLineCancel = async () => {
    if (!mr || !lineCancel) return;
    const reason = lineCancel.reason.trim();
    if (!reason) {
      message.warning('A reason is required');
      return;
    }
    setLineCancelBusy(true);
    try {
      if (lineCancel.direct) {
        await decideMRLineCancel(mr.mrId, lineCancel.line.id, { decision: 'approve', reason });
        message.success(`Line ${lineCancel.line.lineNo} cancelled`);
      } else {
        await requestMRLineCancel(mr.mrId, lineCancel.line.id, reason);
        message.success(`Line ${lineCancel.line.lineNo}: sent to the CEO for approval`);
      }
      setLineCancel(null);
      fetchMR();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to cancel the line');
    } finally {
      setLineCancelBusy(false);
    }
  };
  const decideLineCancel = async (line: MRLine, decision: 'approve' | 'reject') => {
    if (!mr) return;
    setLineCancelBusy(true);
    try {
      await decideMRLineCancel(mr.mrId, line.id, { decision });
      message.success(decision === 'approve' ? `Line ${line.lineNo} cancelled` : `Line ${line.lineNo} kept`);
      fetchMR();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to record the decision');
    } finally {
      setLineCancelBusy(false);
    }
  };

  // Refinement (dialog lives in components/RefineLineModal)
  const [refineModalOpen, setRefineModalOpen] = useState(false);
  const [refiningLine, setRefiningLine] = useState<MRLine | null>(null);

  // Refine is decided per line (approved, not yet on a PO); the column renders only for such lines.
  const canRefine = (isProcurement || isAdmin) && !!mr && !['Draft', 'Submitted', 'Rejected'].includes(mr.status);

  const fetchMR = useCallback(async () => {
    if (!mrId) return;
    setLoading(true);
    try {
      const data = await getMR(mrId!);
      setMr(data);
    } catch (err) {
      message.error('Failed to load MR');
    } finally {
      setLoading(false);
    }
  }, [mrId]);

  useEffect(() => {
    fetchMR();
  }, [fetchMR]);

  // For a line sent back by a PO cancellation the dialog works on the quantity to order again (same as Create PO).
  const openRefineModal = (line: MRLine) => {
    const toOrder = Number(line.reorderQty || 0);
    setRefiningLine(toOrder > 0 ? ({ ...line, qty: toOrder, originalQty: line.qty } as any) : line);
    setRefineModalOpen(true);
  };

  // Cost allocation: approver / procurement / admin may (re)allocate any line at any status.
  const canAllocate = isApprover || isProcurement || isAdmin;
  const handleSetCostCenter = async (lineId: number, costCenterId: number | null) => {
    try {
      await setLineCostCenter(lineId, costCenterId);
      message.success(costCenterId ? 'Cost center updated' : 'Cost center cleared');
      fetchMR();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update cost center');
    }
  };

  // Line-based gates (2026-09-08): a submitted MR can have any pending line decided, and any approved
  // line without a PO refined, whatever the header status says (the header is derived from the lines).
  const isPendingLine = (s: string | undefined | null) => s === 'Pending' || s === 'Submitted' || s === 'Amended';
  const hasPendingLines = (mr?.lines || []).some((l) => isPendingLine(l.lineApprovalStatus || l.status));
  const canApprove = (isApprover || isAdmin) && !!mr && mr.status !== 'Draft' && hasPendingLines;
  // Line cancellation: CEO/admin cancel directly; procurement and the MR's requester ask.
  const canDecideLineCancel = isApprover || isAdmin;
  const canAskLineCancel = !canDecideLineCancel && (isProcurement || mr?.requestedBy?.id === user?.id);
  const isCancellableLine = (l: MRLine) =>
    l.lineApprovalStatus === 'Approved' && l.lineStatus !== 'Cancelled' && l.cancelStatus !== 'Pending' &&
    (!(l.poLines && l.poLines.length > 0) || Number(l.reorderQty || 0) > 0);
  const pendingLineCancels = (mr?.lines || []).filter((l) => l.cancelStatus === 'Pending');

  // Print (2026-09-21): A4 sheet in the PO print's house style. Requesters get a copy without prices, like their screen.
  const handlePrint = () => {
    if (!mr) return;
    const chain = (cc: any): string[] => { const out: string[] = []; let c = cc; while (c) { out.unshift(c.name); c = c.parent; } return out; };
    const html = buildMrPrintHtml({
      mrId: mr.mrId,
      status: mr.status,
      requestDate: (mr as any).requestDate || mr.createdAt,
      neededBy: mr.neededBy,
      urgency: mr.urgency,
      department: mr.department?.name,
      requester: mr.requestedBy?.displayName,
      notes: mr.notes,
      submittedOn: (mr as any).submittedOn,
      approvedBy: mr.approvedBy?.displayName,
      approvedOn: (mr as any).approvedOn,
      lines: (mr.lines || []).map((l) => {
        const names = l.costCenter ? chain(l.costCenter) : [];
        const rejected = l.lineApprovalStatus === 'Rejected';
        return {
          no: l.lineNo,
          code: l.catalogItem?.partNumber || l.partNumber || null,
          en: l.catalogItem?.description || l.itemDescription,
          ar: l.catalogItem?.descriptionAr || null,
          qty: l.qty,
          unit: l.unit?.name,
          price: l.unitPrice,
          total: l.lineTotal,
          vendor: l.vendor?.name,
          approval: l.lineApprovalStatus,
          lineStatus: l.lineStatus,
          cc: names.length > 1 ? names.slice(1).join(' › ') : names[0] || null,
          ccParent: names.length > 1 ? names[0] : null,
          reason: l.lineStatus === 'Cancelled' ? l.cancelReason : rejected ? (l.rejectReason || l.lineNotes) : null,
          notes: rejected ? null : l.lineNotes,
        };
      }),
    }, { showMoney: !isRequesterView, printedAt: dayjs().format('DD MMM YYYY [at] HH:mm'), printedBy: user?.displayName || '' });
    const w = window.open('', '_blank');
    if (!w) { message.error('Allow pop-ups for this site to print'); return; }
    w.document.write(html);
    w.document.close();
    // Wait for the web fonts (brand serif, Arabic) so the first print is not in fallback type.
    const go = () => setTimeout(() => w.print(), 300);
    const fonts = (w.document as any).fonts;
    if (fonts?.ready) Promise.race([fonts.ready, new Promise((r) => setTimeout(r, 2500))]).then(go); else go();
  };

  // Recall to Draft: only the requester who created the MR, only while Submitted, and only while
  // no line has been approved, rejected or refined by anyone.
  const canRecall = ['Submitted', 'Partially Approved'].includes(mr?.status || '') && mr?.requestedBy?.id === user?.id;
  const recallBlockers = (mr?.lines || []).filter((l) => (l.lineApprovalStatus || 'Submitted') !== 'Submitted' || l.refinedAt);
  const recallBlockedReason = recallBlockers.length
    ? `Cannot recall: line${recallBlockers.length > 1 ? 's' : ''} ${recallBlockers.map((l) => l.lineNo).join(', ')} already approved, rejected or refined. Ask the approver to reject them instead.`
    : '';
  const handleRecall = () => {
    if (!mr) return;
    Modal.confirm({
      title: `Recall ${mr.mrId} to Draft?`,
      icon: <RollbackOutlined style={{ color: '#d48806' }} />,
      content: (
        <div>
          The MR leaves the approval queue, goes back to Draft and opens for editing.
          <ul style={{ paddingLeft: 18, marginTop: 8 }}>
            <li>Procurement is notified that it was recalled.</li>
            <li>Nothing is deleted. The recall is written to the change log.</li>
            <li>You must <b>Submit for Approval</b> again when you are done.</li>
          </ul>
        </div>
      ),
      okText: 'Recall to Draft',
      onOk: async () => {
        setActionLoading(true);
        try {
          await recallMR(mr.id);
          message.success('MR recalled to Draft. Make your changes, then submit it again.');
          navigate(`/mrs/${mr.id}/edit`);
        } catch (err: any) {
          message.error(err.response?.data?.error || 'Failed to recall MR');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleSubmit = async () => {
    if (!mr) return;
    setActionLoading(true);
    try {
      await submitMR(mr.id);
      message.success('MR submitted for approval');
      fetchMR();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to submit MR');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveSelected = async () => {
    if (!mr || selectedLines.size === 0) {
      message.warning('Please select lines to approve');
      return;
    }
    setActionLoading(true);
    try {
      const approvals = Array.from(selectedLines).map((lineId) => ({
        lineId,
        approved: true,
      }));
      await approveLines(mr.id, approvals);
      message.success('Lines approved successfully');
      setSelectedLines(new Set());
      fetchMR();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to approve lines');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSelected = () => {
    if (selectedLines.size === 0) {
      message.warning('Please select lines to reject');
      return;
    }
    setRejectTarget('selected');
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!mr || !rejectReason.trim()) {
      message.warning('Please provide a rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      if (rejectTarget === 'single' && singleRejectLineId != null) {
        const approvals = [{
          lineId: singleRejectLineId,
          approved: false,
          rejectReason: rejectReason.trim(),
        }];
        await approveLines(mr.id, approvals);
        message.success('Line rejected');
        setSingleRejectLineId(null);
      } else {
        const approvals = Array.from(selectedLines).map((lineId) => ({
          lineId,
          approved: false,
          rejectReason: rejectReason.trim(),
        }));
        await approveLines(mr.id, approvals);
        message.success('Lines rejected');
        setSelectedLines(new Set());
      }
      setRejectModalOpen(false);
      setRejectReason('');
      fetchMR();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to reject lines');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAmendLine = async (lineId: number) => {
    if (!mr) return;
    const newQty = amendQtys[lineId];
    if (newQty == null || newQty <= 0) {
      message.warning('Please enter a valid quantity');
      return;
    }
    setAmendingLineId(lineId);
    try {
      await amendMRLine(mr.id, lineId, { qty: newQty, lineApprovalStatus: 'Amended' });
      message.success('Line quantity amended');
      setAmendQtys((prev) => { const n = { ...prev }; delete n[lineId]; return n; });
      fetchMR();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to amend line');
    } finally {
      setAmendingLineId(null);
    }
  };

  const handleSingleReject = (lineId: number) => {
    setSingleRejectLineId(lineId);
    setRejectTarget('single');
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const toggleLineSelection = (lineId: number, checked: boolean) => {
    setSelectedLines((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(lineId);
      } else {
        next.delete(lineId);
      }
      return next;
    });
  };

  const isInteractiveStatus = (s: string | undefined | null) =>
    s === 'Pending' || s === 'Submitted' || s === 'Amended';
  const pendingLines = mr?.lines?.filter((l) => isInteractiveStatus(l.lineApprovalStatus || l.status)) || [];
  const allPendingSelected =
    pendingLines.length > 0 && pendingLines.every((l) => selectedLines.has(l.id));

  const toggleAllPending = (checked: boolean) => {
    if (checked) {
      setSelectedLines(new Set(pendingLines.map((l) => l.id)));
    } else {
      setSelectedLines(new Set());
    }
  };

  // Item pictures (2026-09-22): the approver sees what is being asked for without opening the item.
  const lineItemIds = useMemo(() => (mr?.lines || []).map((l) => l.catalogItem?.id ?? l.catalogItemId ?? null), [mr]);
  const { pictures: linePictures, reload: reloadLinePicture } = useItemPictures(lineItemIds);

  // Price prefill (2026-09-29): a requester only gets a price column when they typed an estimate themselves.
  const requesterHasEstimate = (mr?.lines || []).some((l) => Number(l.unitPrice || 0) > 0);

  const lineColumns: any[] = [];

  if (canApprove) {
    lineColumns.push({
      title: (
        <Checkbox
          checked={allPendingSelected}
          indeterminate={selectedLines.size > 0 && !allPendingSelected}
          onChange={(e) => toggleAllPending(e.target.checked)}
        />
      ),
      width: 50,
      render: (_: any, record: MRLine) =>
        isInteractiveStatus(record.lineApprovalStatus || record.status) ? (
          <Checkbox
            checked={selectedLines.has(record.id)}
            onChange={(e) => toggleLineSelection(record.id, e.target.checked)}
          />
        ) : null,
    });
  }

  lineColumns.push(
    {
      title: '#',
      dataIndex: 'lineNo',
      width: 50,
    },
    {
      // Flexible on purpose: the code must never be clipped or wrapped mid-code.
      title: 'Item',
      key: 'item',
      render: (_: any, record: MRLine) => {
        const code = record.catalogItem?.partNumber || record.partNumber;
        const ciId = record.catalogItem?.id ?? record.catalogItemId ?? null;
        const cell = (
          <ItemCell
            partNumber={code}
            description={record.catalogItem?.description || record.itemDescription}
            descriptionAr={record.catalogItem?.descriptionAr}
            linkTo={record.catalogItem?.id ? `/catalog/${record.catalogItem.id}` : undefined}
            compact={compact}
            showThumb
            pictureUrl={ciId ? linePictures[ciId]?.url : null}
            thumbItemId={ciId}
            onThumbError={ciId ? () => reloadLinePicture(ciId) : undefined}
          />
        );
        // 2026-09-16: the same item may repeat on an MR (different cost centers); say so.
        const also = (mr?.lines || []).filter((l) => l.id !== record.id && record.catalogItemId && l.catalogItemId === record.catalogItemId).map((l) => l.lineNo);
        const withAlso = (node: React.ReactNode) => also.length === 0 ? node : (
          <div>{node}<div style={{ fontSize: 11, color: '#8a94a6', marginTop: 2 }}>also on line{also.length > 1 ? 's' : ''} {also.join(', ')}</div></div>
        );
        if (code) return withAlso(cell);
        return (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            {freeTextMark}
            <div style={{ flex: 1, minWidth: 0 }}>{cell}</div>
          </div>
        );
      },
    },
    {
      title: 'Qty',
      key: 'qty',
      width: canApprove ? 130 : 70,
      align: 'right' as const,
      render: (_: any, record: MRLine) => {
        const status = record.lineApprovalStatus || record.status || 'Pending';
        if (canApprove && isInteractiveStatus(status)) {
          return (
            <Space size={2}>
              <InputNumber
                size="small"
                min={0.01}
                value={amendQtys[record.id] ?? Number(record.qty || record.quantity || 0)}
                onChange={(val) => setAmendQtys((prev) => ({ ...prev, [record.id]: val || 0 }))}
                style={{ width: 70 }}
              />
              {amendQtys[record.id] != null && amendQtys[record.id] !== Number(record.qty || record.quantity || 0) && (
                <Popconfirm
                  title="Amend line quantity?"
                  description={`Change qty from ${Number(record.qty || record.quantity || 0)} to ${amendQtys[record.id]}. Line will be tagged Amended and still needs approval.`}
                  okText="Amend"
                  cancelText="Cancel"
                  onConfirm={() => handleAmendLine(record.id)}
                >
                  <Tooltip title="Save amended qty">
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      loading={amendingLineId === record.id}
                    />
                  </Tooltip>
                </Popconfirm>
              )}
            </Space>
          );
        }
        return Number(record.qty || record.quantity || 0);
      },
    },
    {
      title: 'Unit',
      key: 'unit',
      width: 90,
      render: (_: any, record: MRLine) => {
        const catUnit = record.catalogItem?.unit;
        const changed = catUnit && record.unit && catUnit.id !== record.unit.id;
        return (
          <div>
            <div>{record.unit?.name || <MissingValue label="no unit" compact={compact} />}</div>
            {changed && <div style={{ fontSize: 11, color: '#b26a00' }}>changed from {catUnit.name}</div>}
            {record.catalogItem && !catUnit && <div style={{ fontSize: 11, color: '#b26a00' }}>no unit in catalog</div>}
          </div>
        );
      },
    },
    {
      title: 'Approval',
      key: 'lineApprovalStatus',
      width: 110,
      render: (_: any, record: MRLine) => {
        const status = record.lineApprovalStatus || record.status || 'Pending';
        // "Amended" is not a shared purchasing status; it reads as a partial, like the old orange tag.
        return <StatusPill status={status} tone={status === 'Amended' ? 'warn' : undefined} />;
      },
    },
    {
      title: 'PO Status',
      key: 'lineStatus',
      width: 120,
      render: (_: any, record: MRLine) => {
        // PO line cancellation (2026-09-14): what was cancelled on which PO, and what is waiting to be ordered again.
        const poLines: any[] = (record.poLines as any[]) || [];
        const reorder = Number((record as any).reorderQty || 0);
        return (
          <div>
            <StatusPill status={record.lineStatus || 'Open'} />
            {reorder > 0 && <div style={{ fontSize: 11, color: '#9a5b00' }}>{reorder} to order again</div>}
            {poLines.filter((p) => Number(p.cancelledQty || 0) > 0).map((p, i) => (
              <div key={i} style={{ fontSize: 11, color: '#8a94a6' }}>{Number(p.cancelledQty)} cancelled on {p.poHeader?.poId}</div>
            ))}
            {poLines.some((p) => p.cancelStatus === 'Pending') && (
              <div style={{ fontSize: 11, color: '#9a5b00' }}>cancellation pending</div>
            )}
          </div>
        );
      },
    },
    ...(isRequesterView ? [] : [{
      title: 'Vendor',
      key: 'vendor',
      width: 150,
      render: (_: any, record: MRLine) => {
        const source = (record as any).vendorSource as string | undefined;
        return (
          <div>
            {/* appCode is only sent on some vendor payloads; VendorCell hides the tag when absent. */}
            <VendorCell name={record.vendor?.name} appCode={(record.vendor as any)?.appCode} />
            {record.vendor && source && (
              <div style={{ fontSize: 11, color: source === 'procurement' ? '#666' : '#1677ff' }}>
                {source === 'requester' ? "requester's ask" : source === 'department' ? 'department choice' : 'set by procurement'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Category',
      key: 'category',
      width: 120,
      render: (_: any, record: MRLine) => record.category?.name || '-',
    }]),
    // 2026-09-29 price prefill: requesters see only an estimate they typed themselves (R&D, Marketing);
    // everyone else sees the price with where it came from (last paid on a PO, list price, refined, requester est.).
    ...(isRequesterView && !requesterHasEstimate ? [] : [{
      title: 'Est. Price',
      dataIndex: 'unitPrice',
      width: isRequesterView ? 110 : 190,
      align: 'right' as const,
      render: (val: number, record: MRLine) => {
        const price = val == null ? null : Number(val);
        if (isRequesterView) {
          return <MoneyCell amount={price} rule={price ? 'your estimate' : null} align="right" compact={compact} />;
        }
        const filled = onPrefill(record);
        // The rule line never wraps (shared MoneyCell), so it carries only the short part; vendor + date go below.
        const parts = filled ? prefillLabelParts(record) : null;
        const rule = parts
          ? parts.short
          : record.refinedAt || record.origUnitPrice != null ? 'refined' : price ? 'requester est.' : null;
        const history = historyLabel(record);
        const newer = newerPurchase(record, (record.poLines || []).length > 0);
        const catalogDiffers = filled && record.prefillSource === 'po' && record.catalogPrice != null && price != null
          && Math.abs(Number(record.catalogPrice) - price) > 0.005;
        const note: React.CSSProperties = { fontSize: 11, lineHeight: 1.35, textAlign: 'right' };
        return (
          <div>
            <MoneyCell amount={price} rule={rule} align="right" compact={compact} />
            {!compact && parts?.where && <div style={{ ...note, color: '#8a94a6' }}>{parts.where}</div>}
            {!compact && history && <div style={{ ...note, color: '#8a94a6' }}>{history}</div>}
            {!compact && record.requesterEstPrice != null && <div style={{ ...note, color: '#8a94a6' }}>requester est. {fmtPrice(record.requesterEstPrice)}</div>}
            {!compact && newer && <div style={{ ...note, color: '#b26a00' }}>newer price {fmtPrice(newer.price)} on {newer.poId}</div>}
            {!compact && catalogDiffers && <div style={{ ...note, color: '#b26a00' }}>catalog price {fmtPrice(Number(record.catalogPrice))}</div>}
          </div>
        );
      },
    },
    {
      title: 'Currency',
      key: 'currency',
      width: 80,
      render: (_: any, record: MRLine) => record.currency?.code || '-',
    }]),
    ...(isRequesterView ? [] : [{
      title: 'Est. Total',
      key: 'lineTotal',
      width: 110,
      align: 'right' as const,
      render: (_: any, record: MRLine) => (
        <MoneyCell amount={Number(record.lineTotal || record.total || 0)} align="right" compact={compact} />
      ),
    }]),
    ...(isRequesterView ? [] : [{
      title: 'Act. Total',
      key: 'actualPrice',
      width: 140,
      align: 'right' as const,
      render: (_: any, record: MRLine) => {
        const poLines = record.poLines || [];
        const hasActual = poLines.some((po: any) => po.actualLineTotal != null);
        const actual = hasActual
          ? poLines.reduce((sum: number, po: any) => sum + Number(po.actualLineTotal ?? 0), 0)
          : null;
        const paidOn = poLines.find((po: any) => po.actualLineTotal != null && po.poHeader)?.poHeader?.poId;
        return (
          <MoneyCell
            amount={actual}
            rule={actual == null ? null : paidOn ? `paid on ${paidOn}` : 'actual'}
            align="right"
            compact={compact}
            missingLabel={poLines.length ? 'no actual yet' : 'not ordered'}
          />
        );
      },
    }]),
    {
      title: 'Cost Center',
      key: 'costCenter',
      width: 210,
      render: (_: any, record: MRLine) => {
        if (canAllocate) {
          return (
            <CostCenterSelect
              size="small"
              value={record.costCenterId ?? null}
              current={record.costCenter || null}
              placeholder="—"
              onChange={(id) => handleSetCostCenter(record.id, id)}
            />
          );
        }
        return record.costCenter ? costCenterLabel(record.costCenter) : <span style={{ color: '#999' }}>—</span>;
      },
    },
    {
      title: 'PO',
      key: 'po',
      width: 130,
      render: (_: any, record: MRLine | any) => {
        const poLine = record.poLines?.[0];
        if (!poLine?.poHeader) return <span style={{ color: '#999' }}>—</span>;
        const po = poLine.poHeader;
        return (
          <a onClick={() => navigate(`/pos/${po.id}`)} style={{ cursor: 'pointer' }}>
            {po.poId}
          </a>
        );
      },
    },
    {
      title: 'Notes',
      key: 'lineNotes',
      width: 120,
      ellipsis: true,
      render: (_: any, record: MRLine) => {
        if (record.rejectReason) {
          return <span style={{ color: '#ff4d4f' }}>{record.rejectReason}</span>;
        }
        return record.lineNotes || record.notes || '-';
      },
    }
  );

  if (canApprove) {
    lineColumns.push({
      title: 'Actions',
      key: 'lineActions',
      width: 80,
      render: (_: any, record: MRLine) => {
        const status = record.lineApprovalStatus || record.status || 'Pending';
        if (!isInteractiveStatus(status)) return null;
        return (
          <Tooltip title="Reject this line">
            <Button
              type="text"
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => handleSingleReject(record.id)}
            />
          </Tooltip>
        );
      },
    });
  }

  // Refine column for procurement on approved lines
  if (canRefine) {
    lineColumns.push({
      title: 'Refine',
      key: 'refine',
      width: 100,
      render: (_: any, record: MRLine) => {
        const isApprovedLine = record.lineApprovalStatus === 'Approved';
        // 2026-09-17: a line sent back by a PO cancellation (reorderQty > 0) is refinable again, like in Create PO.
        const hasPO = record.poLines && record.poLines.length > 0 && !(Number(record.reorderQty || 0) > 0);
        const isRefined = !!record.refinedAt;
        if (!isApprovedLine || hasPO || record.lineStatus === 'Cancelled' || record.cancelStatus === 'Pending') return isRefined ? <Tag color="blue">Refined</Tag> : '-';
        return (
          <Space size={4}>
            <Tooltip title={isRefined ? 'Re-refine this line' : 'Refine this line'}>
              <Button
                type={isRefined ? 'default' : 'primary'}
                size="small"
                icon={<ToolOutlined />}
                onClick={() => openRefineModal(record)}
              >
                {isRefined ? 'Edit' : 'Refine'}
              </Button>
            </Tooltip>
          </Space>
        );
      },
    } as any);
  }

  // MR line cancellation (2026-09-15): request, pending details + CEO decision, outcome.
  if (mr && mr.status !== 'Draft' && (canDecideLineCancel || canAskLineCancel || (mr.lines || []).some((l) => l.cancelStatus || l.lineStatus === 'Cancelled'))) {
    lineColumns.push({
      title: 'Line action',
      key: 'lineCancel',
      width: 250,
      render: (_: any, record: MRLine) => {
        if (record.cancelStatus === 'Pending') {
          return (
            <div style={{ fontSize: 12, lineHeight: 1.45, whiteSpace: 'normal' }}>
              <StatusPill tone="warn" label="Cancellation pending" />
              <div style={{ marginTop: 4 }}>{record.cancelReason}</div>
              <div style={{ color: '#8a94a6' }}>
                {record.cancelRequestedBy?.displayName || 'requested'}
                {record.cancelRequestedAt ? ` · ${dayjs(record.cancelRequestedAt).format('DD MMM HH:mm')}` : ''}
              </div>
              {canDecideLineCancel && (
                <Space size={6} style={{ marginTop: 6 }}>
                  <Button size="small" loading={lineCancelBusy} onClick={() => decideLineCancel(record, 'reject')}>Reject, keep line</Button>
                  <Popconfirm
                    title={`Cancel line ${record.lineNo}?`}
                    description="It leaves Create PO and stays on the MR as history."
                    okText="Cancel line"
                    okButtonProps={{ danger: true }}
                    cancelText="Back"
                    onConfirm={() => decideLineCancel(record, 'approve')}
                  >
                    <Button size="small" type="primary" danger loading={lineCancelBusy}>Approve</Button>
                  </Popconfirm>
                </Space>
              )}
            </div>
          );
        }
        if (record.lineStatus === 'Cancelled') {
          return (
            <div style={{ fontSize: 12, lineHeight: 1.45, whiteSpace: 'normal' }}>
              <StatusPill status="Cancelled" />
              {record.cancelReason && <div style={{ marginTop: 4 }}>{record.cancelReason}</div>}
              <div style={{ color: '#8a94a6' }}>
                {record.cancelDecidedBy?.displayName || ''}
                {record.cancelDecidedAt ? ` · ${dayjs(record.cancelDecidedAt).format('DD MMM YYYY')}` : ''}
              </div>
            </div>
          );
        }
        if (isCancellableLine(record) && (canDecideLineCancel || canAskLineCancel)) {
          return (
            <div>
              {record.cancelStatus === 'Rejected' && <div style={{ fontSize: 12, color: '#8a94a6' }}>last request rejected</div>}
              <Button size="small" danger onClick={() => setLineCancel({ line: record, reason: '', direct: canDecideLineCancel })}>
                {canDecideLineCancel ? 'Cancel line' : 'Request cancellation'}
              </Button>
            </div>
          );
        }
        if (record.poLines && record.poLines.length > 0 && record.lineApprovalStatus === 'Approved') {
          const po = record.poLines[0]?.poHeader;
          return po ? <span style={{ fontSize: 12, color: '#8a94a6' }}>cancel on <a onClick={() => navigate(`/pos/${po.poId}`)} style={{ cursor: 'pointer' }}>{po.poId}</a></span> : null;
        }
        return null;
      },
    } as any);
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!mr) {
    return <div>MR not found</div>;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/mrs'))}>
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {mr.mrId || `MR-${String(mr.id).padStart(4, '0')}`}
            </Title>
            <Tag color={STATUS_TAG_COLORS[mr.status]} style={{ fontSize: 14, padding: '2px 12px' }}>
              {mr.status}
            </Tag>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>Print</Button>
            {mr.status === 'Draft' && mr.requestedBy?.id === user?.id && (
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/mrs/${mr.id}/edit`)}
              >
                Edit
              </Button>
            )}
            {mr.status === 'Draft' && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={actionLoading}
                onClick={handleSubmit}
              >
                Submit for Approval
              </Button>
            )}
            {canRecall && (
              <Tooltip title={recallBlockedReason || 'Pull this MR back to Draft to edit it, then submit it again'}>
                <span>
                  <Button
                    icon={<RollbackOutlined />}
                    disabled={recallBlockers.length > 0}
                    loading={actionLoading}
                    onClick={handleRecall}
                  >
                    Recall to Draft
                  </Button>
                </span>
              </Tooltip>
            )}
          </Space>
        </Col>
      </Row>

      {canRecall && recallBlockers.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Waiting for approval"
          description="Nobody has acted on this MR yet, so you can still recall it to Draft, change it, and submit it again."
        />
      )}

      {pendingLineCancels.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${pendingLineCancels.length} line cancellation${pendingLineCancels.length === 1 ? '' : 's'} waiting for CEO approval`}
          description={canDecideLineCancel
            ? 'Approve or reject each one in the Line action column.'
            : 'Those lines are held out of Create PO until the CEO decides.'}
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="MR ID">{mr.mrId || `MR-${String(mr.id).padStart(4, '0')}`}</Descriptions.Item>
          <Descriptions.Item label="Department">{mr.department?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="Requested By">{mr.requestedBy?.displayName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Created">{dayjs(mr.createdAt).format('YYYY-MM-DD')}</Descriptions.Item>
          <Descriptions.Item label="Needed By">{dayjs(mr.neededBy).format('YYYY-MM-DD')}</Descriptions.Item>
          <Descriptions.Item label="Urgency">
            <Tag color={URGENCY_TAG_COLORS[mr.urgency]}>{mr.urgency}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={STATUS_TAG_COLORS[mr.status]}>{mr.status}</Tag>
          </Descriptions.Item>
          {/* Price prefill (2026-09-29): requesters see no MR value. */}
          {!isRequesterView && (
            <Descriptions.Item label="Estimated Total">
              {Number(mr.mrTotal || mr.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </Descriptions.Item>
          )}
          {!isRequesterView && Number(mr.cancelledTotal || 0) > 0 && (
            <Descriptions.Item label="Cancelled">
              <span style={{ color: '#cf1322' }}>{Number(mr.cancelledTotal).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              <span style={{ color: '#8a94a6', marginLeft: 8, fontSize: 12 }}>
                live {Number(mr.netTotal ?? ((mr.mrTotal || mr.total || 0) - Number(mr.cancelledTotal))).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </Descriptions.Item>
          )}
          {!isRequesterView && mr.actualTotal != null && (
            <Descriptions.Item label="Actual Total">
              <span style={{ fontWeight: 600 }}>
                {Number(mr.actualTotal).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
              {(() => {
                const est = Number(mr.mrTotal || mr.total || 0);
                const act = Number(mr.actualTotal);
                const diff = act - est;
                if (est === 0) return null;
                const pct = ((diff / est) * 100).toFixed(1);
                const color = diff > 0 ? '#cf1322' : diff < 0 ? '#3f8600' : undefined;
                return <span style={{ color, marginLeft: 8, fontSize: 12 }}>({diff > 0 ? '+' : ''}{pct}%)</span>;
              })()}
            </Descriptions.Item>
          )}
          {/* 2026-09-21: "Allocated to" says what the LINES say (that is what gets charged); the header default is only a prefill. */}
          {(() => {
            const live = (mr.lines || []).filter((l) => l.lineStatus !== 'Cancelled' && l.lineApprovalStatus !== 'Rejected');
            const groups = new Map<string, { label: string; total: number; count: number; missing: boolean }>();
            for (const l of live) {
              const label = l.costCenter ? costCenterLabel(l.costCenter) : 'No cost center';
              const g = groups.get(label) || { label, total: 0, count: 0, missing: !l.costCenter };
              g.total += Number(l.lineTotal || 0); g.count += 1;
              groups.set(label, g);
            }
            const list = Array.from(groups.values()).sort((a, b) => b.total - a.total || b.count - a.count);
            if (list.length === 0) {
              return (mr as any).defaultCostCenter
                ? <Descriptions.Item label="Allocated to" span={3}>{costCenterLabel((mr as any).defaultCostCenter)}</Descriptions.Item>
                : null;
            }
            const share = (g: { total: number; count: number }) => isRequesterView
              ? `${g.count} line${g.count === 1 ? '' : 's'}`
              : g.total.toLocaleString('en-US', { minimumFractionDigits: 2 });
            if (list.length === 1) {
              return (
                <Descriptions.Item label="Allocated to" span={3}>
                  <span style={{ color: list[0].missing ? '#9a5b00' : undefined }}>{list[0].label}</span>
                </Descriptions.Item>
              );
            }
            return (
              <Descriptions.Item label="Allocated to" span={3}>
                <strong>{list.length} cost centers</strong>
                {list.map((g) => (
                  <span key={g.label} style={{ marginLeft: 10, whiteSpace: 'nowrap', color: g.missing ? '#9a5b00' : undefined }}>
                    · {g.label} <span style={{ color: '#8a94a6' }}>({share(g)})</span>
                  </span>
                ))}
              </Descriptions.Item>
            );
          })()}
          {mr.notes && <Descriptions.Item label="Notes" span={3}>{mr.notes}</Descriptions.Item>}
        </Descriptions>
      </Card>

      <Card
        title="Line Items"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <DensityToggle value={density} onChange={setDensity} />
            {canApprove && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleApproveSelected}
                  loading={actionLoading}
                  disabled={selectedLines.size === 0}
                >
                  Approve Selected ({selectedLines.size})
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleRejectSelected}
                  loading={actionLoading}
                  disabled={selectedLines.size === 0}
                >
                  Reject Selected ({selectedLines.size})
                </Button>
              </>
            )}
          </Space>
        }
      >
        <Table
          columns={lineColumns}
          dataSource={mr.lines}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: isRequesterView ? 1686 - (requesterHasEstimate ? 0 : 190) : 2626 }}
        />
      </Card>

      {mr.timeline && mr.timeline.length > 0 && (
        <Card title="Status Timeline">
          <Timeline
            mode="left"
            items={mr.timeline.map((item, idx) => ({
              key: idx,
              label: dayjs(item.createdAt).format('YYYY-MM-DD HH:mm'),
              children: (
                <div>
                  <strong>{item.action}</strong>
                  <div style={{ color: '#666' }}>by {item.userName}</div>
                  {item.details && <div style={{ color: '#999', fontSize: 12 }}>{item.details}</div>}
                </div>
              ),
            }))}
          />
        </Card>
      )}

      {mr && (
        <Card style={{ marginTop: 16 }}>
          <EntityHistory entityType="MR" entityId={mr.mrId} />
        </Card>
      )}

      <Modal
        title={lineCancel ? `${lineCancel.direct ? 'Cancel line' : 'Request cancellation of line'} ${lineCancel.line.lineNo} · ${mr.mrId}` : ''}
        open={!!lineCancel}
        onOk={submitLineCancel}
        onCancel={() => setLineCancel(null)}
        confirmLoading={lineCancelBusy}
        okText={lineCancel?.direct ? 'Cancel line' : 'Send to CEO'}
        okButtonProps={{ danger: true }}
        cancelText="Keep the line"
        width={600}
        destroyOnClose
      >
        {lineCancel && (() => {
          const l = lineCancel.line;
          const qty = Number(l.reorderQty || 0) > 0 ? Number(l.reorderQty) : Number(l.qty || l.quantity || 0);
          const value = qty * Number(l.unitPrice || 0);
          return (
            <div>
              <Typography.Text type="secondary">
                {lineCancel.direct
                  ? 'Takes the line out of Create PO. It stays on the MR as history; nothing is deleted.'
                  : 'Goes to the CEO for approval. The line is held out of Create PO until decided.'}
              </Typography.Text>
              <div style={{ margin: '12px 0' }}>
                <ItemCell
                  partNumber={l.catalogItem?.partNumber || l.partNumber}
                  description={l.catalogItem?.description || l.itemDescription}
                  descriptionAr={l.catalogItem?.descriptionAr}
                />
              </div>
              <Descriptions size="small" bordered column={3} style={{ marginBottom: 14 }}>
                <Descriptions.Item label="Quantity">{qty} {l.unit?.name || ''}</Descriptions.Item>
                {!isRequesterView && <Descriptions.Item label="Est. value">{value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Descriptions.Item>}
                <Descriptions.Item label="Vendor">{l.vendor?.name || '—'}</Descriptions.Item>
              </Descriptions>
              <div><Typography.Text strong>Reason</Typography.Text> <Typography.Text type="danger">*</Typography.Text></div>
              <TextArea
                rows={3}
                maxLength={500}
                value={lineCancel.reason}
                onChange={(e) => setLineCancel((p) => (p ? { ...p, reason: e.target.value } : p))}
                placeholder="Why this line is no longer needed"
                style={{ marginTop: 4 }}
              />
              <div style={{ fontSize: 12, color: '#8a94a6', marginTop: 10 }}>
                Whole line only. To reduce the quantity instead, procurement refines it before ordering.
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        title="Reject Lines - Reason Required"
        open={rejectModalOpen}
        onOk={handleRejectConfirm}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason('');
        }}
        confirmLoading={actionLoading}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <p>Please provide a reason for rejecting {rejectTarget === 'single' ? 'this line' : `the selected ${selectedLines.size} line(s)`}:</p>
        <TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Enter rejection reason..."
        />
      </Modal>

      <RefineLineModal
        line={refiningLine}
        open={refineModalOpen}
        onClose={() => { setRefineModalOpen(false); setRefiningLine(null); }}
        onSaved={fetchMR}
        departmentId={(mr as any)?.departmentId ?? mr?.department?.id ?? null}
      />
    </div>
  );
};

export default MRView;
